import React from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const Commodities = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  const commodityInstruments = [
    { symbol: 'Gold', description: 'Gold Spot', contractSize: '100 USD', marginRate: '1', spread: '0' },
    { symbol: 'Platinum', description: 'Platinum vs Dollar', contractSize: '100 USD', marginRate: '3', spread: '0' },
    { symbol: 'Silver', description: 'Silver Spot', contractSize: '5,000 USD', marginRate: '2', spread: '0' },
    { symbol: 'BRENT', description: 'Crude Oil Brent', contractSize: '1,000 USD', marginRate: '4', spread: '0' },
    { symbol: 'WTI', description: 'Crude Oil West Texas', contractSize: '1,000 USD', marginRate: '4', spread: '0' }
  ];

  const commodityNews = [
    { symbol: 'WTI', bidPrice: '---', askPrice: '---' },
    { symbol: 'BRENT', bidPrice: '---', askPrice: '---' },
    { symbol: 'NGG23', bidPrice: '---', askPrice: '---' },
    { symbol: 'Gold', bidPrice: '---', askPrice: '---' },
    { symbol: 'Silver', bidPrice: '---', askPrice: '---' },
    { symbol: 'Platinum', bidPrice: '---', askPrice: '---' }
  ];

  return (
    <div className="commodities-page">
      {/* Hero Section */}
      <section className="commodities-hero">
        <div className="container">
          <div className="commodities-hero-content">
            <h1 className="commodities-title">{t('markets.commodities.hero.title')}</h1>
            <p className="commodities-subtitle">
              {t('markets.commodities.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('markets.commodities.hero.description')}</h2>
            </div>
            <div className="commodities-features">
              <div className="feature-highlight">
                <i className="fas fa-medal"></i>
                <span>{t('markets.commodities.whyTrade.benefits.0.description')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-gas-pump"></i>
                <span>{t('markets.commodities.whyTrade.benefits.1.description')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-users"></i>
                <span>10,000+ partners</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commodities Instruments Section */}
      <section className="commodities-instruments">
        <div className="container">
          <div className="instruments-header">
            <h2 className="section-title">{t('markets.commodities.instruments.title')}</h2>
            <button className="btn btn-secondary">Go to all instruments</button>
          </div>
          
          <div className="instruments-table-wrapper">
            <table className="instruments-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Description</th>
                  <th>Contract Size</th>
                  <th>Margin Rate</th>
                  <th>Spread Starting From</th>
                </tr>
              </thead>
              <tbody>
                {commodityInstruments.map((instrument, index) => (
                  <tr key={index}>
                    <td className="symbol">{instrument.symbol}</td>
                    <td className="description">{instrument.description}</td>
                    <td>{instrument.contractSize}</td>
                    <td>{instrument.marginRate}</td>
                    <td className="spread">{instrument.spread}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="instruments-note">
            {t('markets.commodities.instruments.note')}
          </p>
        </div>
      </section>

      {/* Why Trade Commodities Section */}
      <section className="why-trade-commodities">
        <div className="container">
          <h2 className="section-title">{t('markets.commodities.whyTrade.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-medal"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.0.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.0.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-gas-pump"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.1.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.1.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-seedling"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.2.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.2.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.3.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.3.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.4.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.4.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3>{t('markets.commodities.whyTrade.benefits.5.title')}</h3>
              <p>{t('markets.commodities.whyTrade.benefits.5.description')}</p>
            </div>
          </div>
          
          <div className="commodities-cta">
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('openAccount', e)}
            >
              {t('nav.openAccount')}
            </button>
          </div>
        </div>
      </section>

      {/* Account Comparison Section */}
      <section className="account-comparison">
        <div className="container">
          <h2 className="section-title">{t('markets.commodities.accountComparison.title')}</h2>
          <div className="accounts-cards">
            
            {/* Pro Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.commodities.accountComparison.accounts.pro')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$20</span>
                  <span className="deposit-label">{t('markets.commodities.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">1 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.commission')}</span>
                  <span className="feature-value">$0</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/pro" className="btn btn-primary account-btn">{t('markets.commodities.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Prime Account */}
            <div className="account-card featured">
              <div className="popular-badge">{t('markets.commodities.accountComparison.mostPopular')}</div>
              <div className="account-header">
                <h3 className="account-name">{t('markets.commodities.accountComparison.accounts.prime')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$250</span>
                  <span className="deposit-label">{t('markets.commodities.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.commission')}</span>
                  <span className="feature-value">$6</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/prime" className="btn btn-primary account-btn">{t('markets.commodities.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Premium Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.commodities.accountComparison.accounts.premium')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$3000</span>
                  <span className="deposit-label">{t('markets.commodities.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.commission')}</span>
                  <span className="feature-value">$3</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.commodities.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.commodities.accountComparison.features.market')}</span>
                </div>
              </div>
              <Link to="/accounts/premium" className="btn btn-primary account-btn">{t('markets.commodities.accountComparison.features.findOutMore')}</Link>
            </div>

          </div>
          
          <div className="demo-account">
            <h3>{t('markets.commodities.accountComparison.demo.title')}</h3>
            <p>{t('markets.commodities.accountComparison.demo.description')}</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('tryDemo', e)}
            >
              {t('markets.commodities.accountComparison.demo.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Commodities News Section */}
      <section className="commodities-news">
        <div className="container">
          <h2 className="section-title">{t('markets.commodities.news.title')}</h2>
          <div className="news-grid">
            {commodityNews.concat(commodityNews, commodityNews).map((commodity, index) => (
              <div key={index} className="news-card">
                <h3 className="commodity-symbol">{commodity.symbol}</h3>
                <div className="price-info">
                  <div className="price-item">
                    <span className="price-label">{t('markets.commodities.news.bidPrice')}</span>
                    <span className="price-value">{commodity.bidPrice}</span>
                  </div>
                  <div className="price-item">
                    <span className="price-label">{t('markets.commodities.news.askPrice')}</span>
                    <span className="price-value">{commodity.askPrice}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('markets.commodities.help.title')}</h2>
          <div className="help-sections">
            <div className="help-column">
              <h3>{t('markets.commodities.help.experienced.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('markets.commodities.help.experienced.partner.title')}</h4>
                  <p>{t('markets.commodities.help.experienced.partner.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('markets.commodities.help.experienced.markets.title')}</h4>
                  <p>{t('markets.commodities.help.experienced.markets.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('markets.commodities.help.experienced.events.title')}</h4>
                  <p>{t('markets.commodities.help.experienced.events.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="help-column">
              <h3>{t('markets.commodities.help.newTrader.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('markets.commodities.help.newTrader.platform.title')}</h4>
                  <p>{t('markets.commodities.help.newTrader.platform.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('markets.commodities.help.newTrader.charts.title')}</h4>
                  <p>{t('markets.commodities.help.newTrader.charts.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('markets.commodities.help.newTrader.demo.title')}</h4>
                  <p>{t('markets.commodities.help.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Commodities;